import { inject, ref } from "vue";
import { useMoney } from "./useMoney";
import { useDeviceFingerprinting } from "./useDeviceFingerprinting";
import { DateTime } from "luxon";
import { createApplePaySession, placeApplePayOrder } from "../api/checkout";
import { updateCountry } from "../api/countries";
import { updateShippingOption, updateShippingRegion } from "../api/shipping";

export function useApplePay() {
    const emitter = inject<any>("mitt");
    const { dollars } = useMoney();
    const { fingerprint } = useDeviceFingerprinting();

    const applePayAvailable = ref(false);
    let shippingTimeId: any = null;

    emitter.on("applePayLoaded", () => initiateApplePay());

    function createPaymentRequest(amount: number) {
        if (!applePayAvailable.value) {
            return;
        }

        const session = new ApplePaySession(3, {
            countryCode: "US",
            currencyCode: "USD",
            supportedNetworks: ["visa", "masterCard", "amex", "discover"],
            merchantCapabilities: ["supports3DS"],
            requiredBillingContactFields: ["postalAddress", "name"],
            requiredShippingContactFields: ["postalAddress", "name", "email"],
            total: {
                label: "Total",
                type: "pending",
                amount: dollars(amount),
            },
        });

        session.onvalidatemerchant = () => {
            createApplePaySession()
                .then((data: any) => {
                    session.completeMerchantValidation(
                        JSON.parse(data.payload.applepaySession),
                    );
                    emitter.emit("applePayOpen", true);
                })
                .catch(() => (applePayAvailable.value = false));
        };

        session.onshippingcontactselected = (event: any) => {
            updateCountry({
                country_code: event.shippingContact.countryCode,
            }).then((data: any) => {
                updateShippingRegion({
                    country_code: event.shippingContact.countryCode,
                    region:
                        data.country.shipping_region_field === "city"
                            ? event.shippingContact.locality
                            : event.shippingContact.postalCode,
                    city: event.shippingContact.locality,
                    state: event.shippingContact.administrativeArea,
                    postal_code: event.shippingContact.postalCode,
                })
                    .then((data: any) => {
                        emitter.emit("shippingRegionUpdated", data);

                        shippingTimeId = data.shippingTimeId;
                        session.completeShippingContactSelection({
                            newTotal: {
                                label: "Total",
                                type: "final",
                                amount: dollars(data.cartTotals.total),
                            },
                            newLineItems: newCartTotals(data.cartTotals),
                            newShippingMethods: newShippingOptions(
                                data.shippingOptions,
                                data.shippingTimeId,
                            ),
                        });
                    })
                    .catch((error) => {
                        if (
                            typeof error.response !== "undefined" &&
                            Object.prototype.hasOwnProperty.call(
                                error.response,
                                "data",
                            ) &&
                            Object.prototype.hasOwnProperty.call(
                                error.response.data,
                                "errors",
                            )
                        ) {
                            emitter.emit("shippingRegionError", {
                                data: error.response.data,
                            });
                        }
                    });
            });
        };

        session.onshippingmethodselected = (event: any) => {
            updateShippingOption(event.shippingMethod.identifier)
                .then((data: any) => {
                    emitter.emit("shippingOptionUpdated", {
                        shippingTimeId: parseInt(
                            event.shippingMethod.identifier,
                        ),
                        ...data,
                    });

                    shippingTimeId = event.shippingMethod.identifier;
                    session.completeShippingMethodSelection({
                        newTotal: {
                            label: "Total",
                            type: "final",
                            amount: dollars(data.cartTotals.total),
                        },
                        newLineItems: newCartTotals(data.cartTotals),
                    });
                })
                .catch((error) => {
                    if (
                        typeof error.response !== "undefined" &&
                        Object.prototype.hasOwnProperty.call(
                            error.response,
                            "data",
                        ) &&
                        Object.prototype.hasOwnProperty.call(
                            error.response.data,
                            "errors",
                        )
                    ) {
                        emitter.emit("shippingOptionError", {
                            data: error.response.data,
                        });
                    }
                });
        };

        session.oncancel = () => emitter.emit("applePayOpen", false);

        session.onpaymentauthorized = (event: any) => {
            placeApplePayOrder({
                shipping_option: shippingTimeId,
                timezone: DateTime.local().toFormat("z"),
                fingerprint: fingerprint.value,
                apple_pay_token: event.payment.token,
                billing_contact: event.payment.billingContact,
                shipping_contact: event.payment.shippingContact,
            })
                .then(() => {
                    session.completePayment({
                        status: ApplePaySession.STATUS_SUCCESS,
                    });
                    window.location = "/checkout/success" as string & Location;
                })
                .catch((error) => {
                    if (
                        typeof error.response !== "undefined" &&
                        Object.prototype.hasOwnProperty.call(
                            error.response,
                            "data",
                        ) &&
                        Object.prototype.hasOwnProperty.call(
                            error.response.data,
                            "errors",
                        )
                    ) {
                        session.completePayment({
                            status: ApplePaySession.STATUS_FAILURE,
                        });

                        if (
                            Object.prototype.hasOwnProperty.call(
                                error.response.data.errors,
                                "apple_pay_token",
                            )
                        ) {
                            emitter.emit(
                                "newStatusMessage",
                                error.response.data.errors.apple_pay_token[0],
                            );
                        }

                        emitter.emit("applePayOpen", false);
                    }
                });
        };

        try {
            session.begin();
        } catch {
            // Prevent duplicate payment sheet error
        }
    }

    function initiateApplePay() {
        if (window.location.protocol !== "https:") {
            return;
        }

        if (typeof ApplePaySession !== "undefined") {
            applePayAvailable.value = ApplePaySession.canMakePayments();
            ApplePaySession.applePayCapabilities(
                import.meta.env.VITE_APPLE_PAY_MERCHANT_IDENTIFIER,
            ).then((capabilities: any) => {
                switch (capabilities.paymentCredentialStatus) {
                    case "applePayUnsupported":
                        return;

                    default:
                        emitter.emit("alternateCheckoutAvailable", true);
                }
            });
        }
    }

    function newCartTotals(cartTotals: any) {
        const newCartTotals = [
            {
                label: "Subtotal",
                amount: dollars(cartTotals.subtotal),
            },
        ];

        if (cartTotals.discounts) {
            newCartTotals.push({
                label: "Discounts",
                amount: `-${dollars(cartTotals.discounts)}`,
            });
        }

        if (cartTotals.tax) {
            newCartTotals.push({
                label: "Tax",
                amount: `${dollars(cartTotals.tax)}`,
            });
        }

        newCartTotals.push({
            label: "Shipping",
            amount: dollars(cartTotals.shipping),
        });

        return newCartTotals;
    }

    function newShippingOptions(
        shippingOptions: any,
        selectedShippingOptionId: any,
    ) {
        const newShippingOptions = [];

        for (const key in shippingOptions) {
            if (Object.prototype.hasOwnProperty.call(shippingOptions, key)) {
                newShippingOptions.push({
                    label: `${_.capitalize(shippingOptions[key].type.toLowerCase())} Shipping`,
                    detail: shippingOptions[key].estimatedDelivery,
                    amount: dollars(shippingOptions[key].total),
                    identifier: shippingOptions[key].id,
                });
            }
        }

        return newShippingOptions.sort((first, second) => {
            return first.identifier === selectedShippingOptionId
                ? -1
                : second.identifier === selectedShippingOptionId
                  ? 1
                  : 0;
        });
    }

    return {
        applePayAvailable,
        createPaymentRequest,
    };
}
